import { CSSProperties, useEffect, useMemo, useState } from 'react'
import { DroplistProduct } from 'src/types/Droplist'
import styled from 'styled-components'
import {
    Badge,
    EmptyRow,
    Grid,
    Input,
    Label,
    Muted,
    Pager,
    PrimaryButton,
    Row,
    SecondaryButton,
    Table,
    TableWrap,
    TextArea,
} from '../affiliate/ui'
import { Actions, Mono, SmallButtonStyle, useDebounced } from '../admin-affiliates/shared'

const PER_PAGE = 50
const PHONE = '(max-width: 700px)'

/** Phones get a card per product instead of a seven-column table they'd have to scroll sideways. */
const usePhone = () => {
    const [phone, setPhone] = useState(() => window.matchMedia(PHONE).matches)
    useEffect(() => {
        const query = window.matchMedia(PHONE)
        const onChange = () => setPhone(query.matches)
        query.addEventListener('change', onChange)
        return () => query.removeEventListener('change', onChange)
    }, [])
    return phone
}

const CardList = styled.ul`
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const ProductCard = styled.li`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.06);
    min-width: 0;
`

const Meta = styled.p`
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    overflow-wrap: anywhere;
`

const CardButtons = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
`

const Thumb = styled.img`
    width: 44px;
    height: 44px;
    border-radius: 8px;
    object-fit: cover;
    background: rgba(255, 255, 255, 0.06);
    display: block;
`

const NameLink = styled.a`
    color: #8fa0ff;
    font-weight: 600;
    overflow-wrap: anywhere;
`

const Keywords = styled(Mono)`
    display: block;
    margin-top: 2px;
    word-break: normal;
    overflow-wrap: anywhere;
`

const lines = (list: string[]) => list.join('\n')
const fromLines = (text: string) =>
    text
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)

const summary = (list: string[]) =>
    list.length === 0 ? '—' : list.length <= 3 ? list.join(', ') : `${list.slice(0, 3).join(', ')} +${list.length - 3}`

const EditForm = ({
    product,
    onSave,
    onCancel,
}: {
    product: DroplistProduct
    onSave: (product: DroplistProduct) => void
    onCancel: () => void
}) => {
    const [name, setName] = useState(product.name)
    const [category, setCategory] = useState(product.category)
    const [price, setPrice] = useState(product.price)
    const [keywords, setKeywords] = useState(product.keywords)
    const [image, setImage] = useState(product.image)
    const [sizes, setSizes] = useState(lines(product.sizes))
    const [colors, setColors] = useState(lines(product.colors))

    const save = () =>
        onSave({
            ...product,
            name: name.trim(),
            category: category.trim(),
            price: price.trim().replace(/^\$/, ''),
            keywords: keywords.trim(),
            image: image.trim(),
            sizes: fromLines(sizes),
            colors: fromLines(colors),
        })

    return (
        <EditBox>
            <Grid $min={200}>
                <Label>
                    Name
                    <Input value={name} onChange={(e) => setName(e.target.value)} />
                </Label>
                <Label>
                    Category
                    <Input value={category} onChange={(e) => setCategory(e.target.value)} />
                </Label>
                <Label>
                    Price (USD)
                    <Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
                </Label>
            </Grid>
            <Grid $min={200}>
                <Label>
                    Keywords
                    <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} />
                </Label>
                <Label>
                    Image URL
                    <Input type="url" value={image} onChange={(e) => setImage(e.target.value)} />
                </Label>
            </Grid>
            <Grid $min={200}>
                <Label>
                    Sizes (one per line)
                    <TextArea value={sizes} onChange={(e) => setSizes(e.target.value)} />
                </Label>
                <Label>
                    Colour keywords (one per line)
                    <TextArea value={colors} onChange={(e) => setColors(e.target.value)} />
                </Label>
            </Grid>
            <EditButtons>
                <PrimaryButton type="button" disabled={!name.trim()} onClick={save}>
                    Done
                </PrimaryButton>
                <SecondaryButton type="button" onClick={onCancel}>
                    Cancel
                </SecondaryButton>
            </EditButtons>
        </EditBox>
    )
}

const EditBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
`

// Side by side on desktop; full-width, easy-to-hit buttons on a phone.
const EditButtons = styled.div`
    display: flex;
    gap: 10px;

    @media ${PHONE} {
        display: grid;
        grid-template-columns: 1fr 1fr;
    }
`

const ProductName = ({ product }: { product: DroplistProduct }) =>
    /^https?:\/\//i.test(product.href) ? (
        <NameLink href={product.href} target="_blank" rel="noopener noreferrer">
            {product.name}
        </NameLink>
    ) : (
        <span style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{product.name}</span>
    )

/**
 * The parsed products, editable in place. Changes stay local until the parent saves them, so the
 * list only reports a new list through onChange.
 */
const ProductsTable = ({
    products,
    onChange,
    readOnly,
}: {
    products: DroplistProduct[]
    onChange: (products: DroplistProduct[]) => void
    readOnly: boolean
}) => {
    const phone = usePhone()
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    // Index into `products`, not into the filtered page, so editing survives a search change.
    const [editing, setEditing] = useState<number | null>(null)
    const q = useDebounced(search.trim().toLowerCase(), 200)

    const rows = useMemo(
        () =>
            products
                .map((product, index) => ({ product, index }))
                .filter(({ product }) => !q || `${product.name} ${product.category}`.toLowerCase().includes(q)),
        [products, q]
    )
    const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE))
    useEffect(() => setPage(1), [q])
    useEffect(() => {
        if (page > totalPages) setPage(totalPages)
    }, [page, totalPages])
    useEffect(() => {
        if (readOnly) setEditing(null)
    }, [readOnly])

    const visible = rows.slice((page - 1) * PER_PAGE, page * PER_PAGE)
    const empty = products.length ? 'No products match.' : 'No products.'

    const save = (index: number) => (next: DroplistProduct) => {
        onChange(products.map((p, i) => (i === index ? next : p)))
        setEditing(null)
    }
    const remove = (index: number) => {
        setEditing(null)
        onChange(products.filter((_, i) => i !== index))
    }

    const buttons = (product: DroplistProduct, index: number, style?: CSSProperties) => (
        <>
            <SecondaryButton type="button" style={style} disabled={readOnly} onClick={() => setEditing(index)}>
                Edit
            </SecondaryButton>
            <SecondaryButton
                type="button"
                style={style}
                disabled={readOnly}
                aria-label={`Remove ${product.name}`}
                onClick={() => remove(index)}
            >
                Remove
            </SecondaryButton>
        </>
    )

    return (
        <>
            <Row>
                <Input
                    type="search"
                    style={{ maxWidth: phone ? undefined : 320 }}
                    placeholder="Search name or category"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                {q && (
                    <Muted>
                        {rows.length} of {products.length}
                    </Muted>
                )}
            </Row>
            {phone ? (
                <CardList>
                    {visible.length === 0 ? (
                        <Muted as="li">{empty}</Muted>
                    ) : (
                        visible.map(({ product, index }) => (
                            <ProductCard key={index}>
                                {editing === index ? (
                                    <EditForm product={product} onCancel={() => setEditing(null)} onSave={save(index)} />
                                ) : (
                                    <>
                                        <Row $wrap={false} $gap={12} style={{ alignItems: 'flex-start' }}>
                                            {product.image ? <Thumb src={product.image} alt="" loading="lazy" /> : null}
                                            <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                <ProductName product={product} />
                                                <Row $gap={6}>
                                                    {product.category && <Badge>{product.category}</Badge>}
                                                    <span style={{ fontSize: 14, fontWeight: 600 }}>
                                                        {product.price ? `$${product.price}` : '—'}
                                                    </span>
                                                </Row>
                                            </div>
                                        </Row>
                                        <Meta>Sizes: {summary(product.sizes)}</Meta>
                                        {product.colors.length > 0 && <Meta>Colours: {summary(product.colors)}</Meta>}
                                        <Keywords>{product.keywords}</Keywords>
                                        <CardButtons>{buttons(product, index)}</CardButtons>
                                    </>
                                )}
                            </ProductCard>
                        ))
                    )}
                </CardList>
            ) : (
                <TableWrap>
                    <Table>
                        <thead>
                            <tr>
                                <th aria-label="Image" />
                                <th>Name</th>
                                <th>Category</th>
                                <th className="num">Price</th>
                                <th>Sizes</th>
                                <th>Colours</th>
                                <th aria-label="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {visible.length === 0 ? (
                                <EmptyRow colSpan={7}>{empty}</EmptyRow>
                            ) : (
                                visible.map(({ product, index }) =>
                                    editing === index ? (
                                        <tr key={index}>
                                            <td colSpan={7}>
                                                <EditForm
                                                    product={product}
                                                    onCancel={() => setEditing(null)}
                                                    onSave={save(index)}
                                                />
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr key={index}>
                                            <td>{product.image ? <Thumb src={product.image} alt="" loading="lazy" /> : null}</td>
                                            <td style={{ minWidth: 220 }}>
                                                <ProductName product={product} />
                                                <Keywords>{product.keywords}</Keywords>
                                            </td>
                                            <td>
                                                {product.category ? <Badge>{product.category}</Badge> : <Muted as="span">—</Muted>}
                                            </td>
                                            <td className="num">{product.price ? `$${product.price}` : '—'}</td>
                                            <td style={{ minWidth: 140 }}>{summary(product.sizes)}</td>
                                            <td style={{ minWidth: 120 }}>{summary(product.colors)}</td>
                                            <td>
                                                <Actions style={{ flexWrap: 'nowrap' }}>
                                                    {buttons(product, index, SmallButtonStyle)}
                                                </Actions>
                                            </td>
                                        </tr>
                                    )
                                )
                            )}
                        </tbody>
                    </Table>
                </TableWrap>
            )}
            <Pager page={page} totalPages={totalPages} onChange={setPage} />
        </>
    )
}

export default ProductsTable
